import moment from "moment"
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

import { Badge } from "@/components/elements/Badge";
import { CustomBadge } from "@/components/elements/CustomBadge";
import { ShieldCheck, ShieldX, BadgeCent } from "lucide-react";
import Tooltip from "@/components/elements/Tooltip";
 
/* ============== Class niiluuleh ============== */

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function classNames(...classes: ClassValue[]) {
  return classes.filter(Boolean).join(' ')
}

const cyrillicToMongolianLatin = {
  'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo', 
  'ж': 'j', 'з': 'z', 'и': 'i', 'й': 'i', 'к': 'k', 'л': 'l', 'м': 'm', 
  'н': 'n', 'о': 'o', 'ө': 'u', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 
  'у': 'u', 'ү': 'u', 'ф': 'f', 'х': 'kh', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 
  'щ': 'sh', 'ъ': '', 'ы': 'ii', 'ь': 'i', 'э': 'e', 'ю': 'yu', 'я': 'ya',
  'А': 'a', 'Б': 'b', 'В': 'v', 'Г': 'g', 'Д': 'd', 'Е': 'e', 'Ё': 'yo', 
  'Ж': 'j', 'З': 'z', 'И': 'i', 'Й': 'i', 'К': 'k', 'Л': 'l', 'М': 'm', 
  'Н': 'n', 'О': 'o', 'Ө': 'u', 'П': 'p', 'Р': 'r', 'С': 's', 'Т': 't', 
  'У': 'u', 'Ү': 'u', 'Ф': 'f', 'Х': 'kh', 'Ц': 'ts', 'Ч': 'ch', 'Ш': 'sh', 
  'Щ': 'sh', 'Ъ': '', 'Ы': 'ii', 'Ь': 'i', 'Э': 'e', 'Ю': 'yu', 'Я': 'ya'
} as any

export const transliterateCyrillicToLatin = (text: string) => {
  return text.split('').map(char => cyrillicToMongolianLatin[char] || char).join('');
}

export const convertDomainName = (text: string) => {
  return transliterateCyrillicToLatin(text).toString().toLowerCase().trim().replace(/[\s\W-]+/g, '-').replace(/^-+|-+$/g, '');
}

/* ============== Check inputs ============== */

export const isValidEmail = (email: string) => { // only email format
  if(!email) return false
  const regex = new RegExp(/^[-!#-'*+\/-9=?^-~]+(?:\.[-!#-'*+\/-9=?^-~]+)*@[-!#-'*+\/-9=?^-~]+(?:\.[-!#-'*+\/-9=?^-~]+)+$/, "i")
  return regex.test(email)
}

export const isValidPhoneNumber = (phone: string) => { // all types of phone numbers
  if(!phone) return false
  if(phone.length < 6) return false
  const regex = new RegExp(/^[+]?(?:\(\d+(?:\.\d+)?\)|\d+(?:\.\d+)?)(?:[ -]?(?:\(\d+(?:\.\d+)?\)|\d+(?:\.\d+)?))*(?:[ ]?(?:x|ext)\.?[ ]?\d{1,5})?$/)
  return regex.test(phone)
}

export const isValidName = (name: string) => { // Mongolian and English letters, hyphen, apostrophe and space
  if(!name) return false
  const regex = new RegExp('^[\u04ae\u04af\u04e8\u04e9\u0401\u0451\u0410-\u044fa-zA-Z-\' ]+$')
  return regex.test(name)
}

export const isValidUsername = (username: string) => { // only letters, numbers, dots and underscores
  if(!username) return false
  const regex = new RegExp(/^[a-zA-Z0-9._]+$/)
  return regex.test(username)
}

export const isNotEmpty = (value: any) => {
  if(value === undefined || value === null || value === '') return false
  let check = value.split(' ').join('').split('\n').join('')
  if(check === '') return false
  return true
}


/* ============== Check JS variables ============== */

export const isValidBoolean = (something: boolean) => {
  if(typeof something === "boolean") return true
  else return false
}

export const isValidArray = (array: Array<any>) => {
  if(Array.isArray(array)) return true
  else return false
}


/* ============== Utils ============== */

export function formatImageURL(image: string, root = false){ // Use this function if you are using images from bucket
  const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
  let bucket = ''
  if(process.env.NEXT_PUBLIC_BUCKET){
    if(root) bucket = process.env.NEXT_PUBLIC_BUCKET
    else bucket = process.env.NEXT_PUBLIC_BUCKET + 'uploads/'
  }
  if(urlPattern.test(image)) return image
  else return bucket ? bucket + image : image
}

export function formatSupportImageURL(image: string){ // Help-chat upload bucket
  const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
  let bucket = ''
  if(process.env.NEXT_PUBLIC_BUCKET){
    bucket = process.env.NEXT_PUBLIC_BUCKET + 'support/'
  }
  if(urlPattern.test(image)) return image
  else return bucket ? bucket + image : image
}

export function formatNumber(number: any){ // 12000 => 12,000
  number = number.toString()
  var pattern = /(-?\d+)(\d{3})/
  while (pattern.test(number))
    number = number.replace(pattern, "$1,$2")
  return number
}

export function formatPrice(amount: any): string { // 12000 => 12,000₮
  if(amount === undefined || amount === null) return ''
  amount = amount.toString()
  var pattern = /(-?\d+)(\d{3})/
  while (pattern.test(amount))
    amount = amount.replace(pattern, "$1,$2")
  return amount + '₮'
}

// laterDelivery vendors store deliveryPrice 0 because the fee is agreed with the
// customer separately — rendering "Үнэгүй" there claims delivery is free when it is not.
export function formatDeliveryPrice(deliveryPrice: any, deliveryType?: string): string {
  if(deliveryPrice) return formatPrice(deliveryPrice)
  return deliveryType === 'laterDelivery' ? 'Нэмэлтээр тооцно' : 'Үнэгүй'
}

export function formatAICredits(credits: number | string, iconSize: number = 14){
  const formatted = formatNumber(credits)
  return (
    <span className="inline-flex items-center gap-0.5">
      {formatted}
      <BadgeCent size={iconSize} className="text-muted-foreground shrink-0" />
    </span>
  )
}

// Discount utility functions

// Helper to extract specific discount type from discounts array
export function getDiscountByType(discounts: any[] | null | undefined, type: string): any | null {
  if (!discounts || !Array.isArray(discounts)) return null
  return discounts.find(d => d.type === type) || null
}

// Helper to get productDiscount from discounts array
export function getProductDiscount(discounts: any[] | null | undefined): any | null {
  return getDiscountByType(discounts, 'productDiscount')
}

// Helper to get buyXgetY discount from discounts array
export function getBuyXGetYDiscount(discounts: any[] | null | undefined): any | null {
  return getDiscountByType(discounts, 'buyXgetY')
}

// Helper to get freeShipping discount from discounts array
export function getFreeShippingDiscount(discounts: any[] | null | undefined): any | null {
  return getDiscountByType(discounts, 'freeShipping')
}

// Check if a discount applies to a specific variant
// If variantIds is null/undefined/empty, the discount applies to all variants
// If variantIds has values, the discount only applies if the variant's id is in the array
function discountAppliesToVariant(discount: any, variant: any): boolean {
  if (!discount || !variant) return false
  if (!discount.variantIds || discount.variantIds.length === 0) return true
  return discount.variantIds.includes(variant.id)
}

export function getDisplayPrice(variant: any, discounts: any[] | any | null) {
  if (!variant) return null

  // Support both old single discount and new discounts array format
  const productDiscount = Array.isArray(discounts) ? getProductDiscount(discounts) : discounts

  // If automatic discount exists (not code method), calculate discount
  // Code method discounts only apply when customer enters the code at checkout
  // Also check if the discount applies to this specific variant
  if (productDiscount && productDiscount.type === 'productDiscount' && productDiscount.method !== 'code' && discountAppliesToVariant(productDiscount, variant)) {
    // If discountBase is 'sale', calculate from sale price if exists, otherwise from original
    const basePrice = productDiscount.discountBase === 'sale' && variant.priceSale ? variant.priceSale : variant.price
    if (productDiscount.valueType === 'percent') {
      return Math.max(0, Math.round(basePrice * (1 - productDiscount.valuePercent / 100)))
    } else if (productDiscount.valueType === 'amount') {
      return Math.max(0, basePrice - productDiscount.valueAmount)
    } else if (productDiscount.valueType === 'free') {
      return 0
    }
  }

  // No automatic product discount - use sale price or regular price
  // buyXgetY and order discounts don't affect per-item display price
  return variant.priceSale || variant.price
}

export function getOriginalPrice(variant: any, discounts: any[] | any | null) {
  if (!variant) return null

  // Support both old single discount and new discounts array format
  const productDiscount = Array.isArray(discounts) ? getProductDiscount(discounts) : discounts

  // If there's an automatic discount (not code method) that applies to this variant, show original price as crossed-out
  // Code method discounts don't show crossed price since they only apply with code
  if (productDiscount && productDiscount.type === 'productDiscount' && productDiscount.method !== 'code' && discountAppliesToVariant(productDiscount, variant)) {
    return variant.price
  }

  // If only sale price, show original price crossed out
  // buyXgetY and order discounts don't affect per-item display, so sale price still applies
  if (variant.priceSale && variant.priceSale < variant.price) {
    return variant.price
  }

  return null // No original price to show
}

export function shouldShowBuyXGetYBadge(discounts: any[] | any | null) {
  // Support both old single discount and new discounts array format
  const buyXgetY = Array.isArray(discounts) ? getBuyXGetYDiscount(discounts) : discounts

  return buyXgetY
    && buyXgetY.type === 'buyXgetY'
    && buyXgetY.role === 'trigger' // Only show on trigger products
}

export function getBuyXGetYBadgeText(discounts: any[] | any | null, productId?: string) {
  // Support both old single discount and new discounts array format
  const buyXgetY = Array.isArray(discounts) ? getBuyXGetYDiscount(discounts) : discounts

  if (!buyXgetY || buyXgetY.type !== 'buyXgetY') return ''

  const rewardProducts = buyXgetY.rewardProducts || []

  // Check if this trigger product is also one of the reward products (same product X=Y)
  const isSameProduct = productId && rewardProducts.some((rp: any) => rp.id === productId)

  if (isSameProduct) {
    return `${buyXgetY.buyXMinQuantity} + ${buyXgetY.getYMaxQuantity} үнэгүй`
  }

  // Different product — show that there's a reward
  return `${buyXgetY.buyXMinQuantity} + бэлэг`
}

export function getDiscountPercent(variant: any, discounts: any[] | any | null) {
  // Support both old single discount and new discounts array format
  const productDiscount = Array.isArray(discounts) ? getProductDiscount(discounts) : discounts

  if (!variant || !productDiscount || productDiscount.type !== 'productDiscount') return null
  if (!discountAppliesToVariant(productDiscount, variant)) return null

  const originalPrice = variant.price
  const displayPrice = getDisplayPrice(variant, discounts)

  if (!displayPrice || displayPrice >= originalPrice) return null

  return Math.round(((originalPrice - displayPrice) / originalPrice) * 100)
}

export function timeAgo(date: Date) { // 2024-04-10T12:00:00.000Z => 1ц өмнө
  const now = new Date();
  date = new Date(date);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) {
    return `Яг одоо`
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}${minutes === 1 ? 'м' : 'м'} өмнө`
  } else if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return `${hours}${hours === 1 ? 'ц' : 'ц'} өмнө`
  } else if (seconds < 2592000) {
    const days = Math.floor(seconds / 86400);
    return `${days}${days === 1 ? 'ө' : 'ө'} өмнө`
  } else if (seconds < 31536000) {
    const months = Math.floor(seconds / 2592000);
    return `${months}${months === 1 ? ' сарын' : 'сарын'} өмнө`
  } else {
    const years = Math.floor(seconds / 31536000);
    return `${years}${years === 1 ? ' жилийн' : 'жилийн'} өмнө`
  }
}

export function timeAgoShort(date: Date) { // 2024-04-10T12:00:00.000Z => 1ц
  const now = new Date();
  date = new Date(date);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) {
    return `Одоо`
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}${minutes === 1 ? 'м' : 'м'}`
  } else if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return `${hours}${hours === 1 ? 'ц' : 'ц'}`
  } else if (seconds < 2592000) {
    const days = Math.floor(seconds / 86400);
    return `${days}${days === 1 ? 'ө' : 'ө'}`
  } else if (seconds < 31536000) {
    const months = Math.floor(seconds / 2592000);
    return `${months}${months === 1 ? ' сар' : 'сар'}`
  } else {
    const years = Math.floor(seconds / 31536000);
    return `${years}${years === 1 ? ' жил' : 'жил'}`
  }
}

export function formatDateNormal(date: Date){ // 2024-04-10T12:00:00.000Z => 2024.04.10
  date = new Date(date);
  var day = String(date.getDate()).padStart(2, '0');
  var month = String(date.getMonth() + 1).padStart(2, '0');
  var year = String(date.getFullYear());
  return `${year}.${month}.${day}`;
}

export function formatDateTimeNormal(date: Date){ // 2024-04-10T12:00:00.000Z => 2024.04.10 12:00
  date = new Date(date);
  var day = String(date.getDate()).padStart(2, '0');
  var month = String(date.getMonth() + 1).padStart(2, '0');
  var year = String(date.getFullYear());
  var hours = String(date.getHours()).padStart(2, '0');
  var minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}.${month}.${day} ${hours}:${minutes}`;
}

export function formatDateLong(date: Date){ // 2024-04-10T12:00:00.000Z => 2024 оны 04-р сарын 10
  date = new Date(date);
  var day = String(date.getDate());
  var month = String(date.getMonth() + 1);
  var year = String(date.getFullYear());
  return `${year} оны ${month}-р сарын ${day}`;
}

export function formatDateTimeLong(date: Date){ // 2024-04-10T12:00:00.000Z => 2024 оны 04-р сарын 10 12:00
  date = new Date(date);
  var day = String(date.getDate());
  var month = String(date.getMonth() + 1);
  var year = String(date.getFullYear());
  var hours = String(date.getHours()).padStart(2, '0');
  var minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year} оны ${month}-р сарын ${day} ${hours}:${minutes}`;
}

export function calcBetweenDates(start: Date, end: Date){ // 2024-04-10T12:00:00.000Z, 2024-04-15T12:00:00.000Z => 5
  let startDate = new Date(start)
  let endDate = new Date(end)
  let diff = endDate.getTime() - startDate.getTime()
  let days = diff / (1000 * 60 * 60 * 24)
  return days + 1
}

export function formatTime(seconds: number){ // 3600 => 01:00:00
  let hours = Math.floor(seconds / 3600)
  let minutes = Math.floor(seconds / 60) - (hours * 60)
  seconds = seconds % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}


export function urlBase64ToUint8Array(base64String: any) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replace(/\\-/g, '+')
    .replace(/_/g, '/')
 
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
 
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export function bytesToSize(bytes: number) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes == 0) return '0 Byte';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString());
  return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
}

export const formatTextLinks = (text: string) => {
  if (!text) return ''
  const urlPattern = /((http|https):\/\/[^\s/$.?#].[^\s]*)|(www\.[^\s/$.?#].[^\s]*)/gi
  return text.replace(urlPattern, (match) => {
    const url = match.startsWith('http') ? match : `https://${match}`
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="underline font-semibold">${match}</a>`
  })
}

export type RoleType = 'superadmin' | 'manager' | 'sales' | 'courier' | 'content' | 'accountant'

export function checkRole({ role, allow, deny, isSuperAdmin = false }: { role: RoleType, allow?: RoleType | RoleType[], deny?: RoleType | RoleType[], isSuperAdmin?: boolean }) {
  if(isSuperAdmin) return true
  if(!role) return false
  if(!allow && !deny) return true
  if(allow && Array.isArray(allow)) {
    if(allow.includes(role)) return true
    else return false
  }
  if(deny && Array.isArray(deny)) {
    if(deny.includes(role)) return false
    else return true
  }
  if(allow && typeof allow === 'string' && allow == role) return true
  if(deny && typeof deny === 'string' && deny == role) return false
  return false
}

// Check a (resource, action) against a resolved permissions map.
// Use `usePermission` from PermissionsProvider in React components — this helper is for non-React contexts.
export function checkPermission(permissions: any, resource: string, action: string): boolean {
  if (permissions === 'ALL') return true
  if (!permissions || typeof permissions !== 'object') return false
  const actions = permissions[resource]
  return Array.isArray(actions) && actions.includes(action)
}


export function removeHTMLTags(str: string) {
  if (!str) return '';
  return str.replace(/<\/?[^>]+(>|$)/g, '');
}

export function checkPathname(pathname: string | null) {
  if(pathname === null) return '/'
  let pathnames = pathname.split('/')
  return '/' + pathnames[1]
}


export const adminRoleList = [
  { value: 'superadmin', label: 'Системийн админ' },
  { value: 'manager', label: 'Менежер' },
  { value: 'sales', label: 'Борлуулалтын ажилтан' },
  { value: 'courier', label: 'Хүргэлтийн ажилтан' },
  { value: 'content', label: 'Контент менежер' },
  { value: 'accountant', label: 'Нягтлан бодогч' }
]

export function getAdminRoleBadge(role: string) {
  switch (role) {
    case 'owner':
      return <Badge variant="secondary">Дэлгүүрийн эзэн</Badge>
    case 'superadmin':
      return <Badge variant="secondary">Системийн админ</Badge>
    case 'manager':
      return <Badge variant="secondary">Менежер</Badge>
    case 'sales':
      return <Badge variant="secondary">Борлуулалтын ажилтан</Badge>
    case 'courier':
      return <Badge variant="secondary">Хүргэлтийн ажилтан</Badge>
    case 'content':
      return <Badge variant="secondary">Контент менежер</Badge>
    case 'accountant':
      return <Badge variant="secondary">Нягтлан бодогч</Badge>
    default:
      return <Badge variant="secondary">Тодорхойгүй</Badge>
  }
}

export function getAdminRoleText(role: string) {
  switch (role) {
    case 'superadmin':
      return 'Системийн админ'
    case 'manager':
      return 'Менежер'
    case 'sales':
      return 'Борлуулалтын ажилтан'
    case 'courier':
      return 'Хүргэлтийн ажилтан'
    case 'content':
      return 'Контент менежер'
    case 'accountant':
      return 'Нягтлан бодогч'
    default:
      return 'Тодорхойгүй'
  }
}

export function getInvoiceStatusBadge(status: string) {
  switch (status) {
    case 'pending':
      return <CustomBadge color="amber">Хүлээгдэж буй</CustomBadge>
    case 'paid':
      return <CustomBadge color="green">Төлөгдсөн</CustomBadge>
    case 'canceled':
      return <CustomBadge color="red">Цуцлагдсан</CustomBadge>
    case 'refunded':
      return <CustomBadge color="purple">Буцаагдсан</CustomBadge>
    default:
      return <CustomBadge>Тодорхойгүй</CustomBadge>
  }
}

export function getInvoiceStatusText(status: string) {
  switch (status) {
    case 'pending':
      return 'Хүлээгдэж буй'
    case 'paid':
      return 'Төлөгдсөн'
    case 'canceled':
      return 'Цуцлагдсан'
    case 'refunded':
      return 'Буцаагдсан'
    default:
      return 'Тодорхойгүй'
  }
}

export function getInvoiceMethodText(status: string) {
  switch (status) {
    case 'qpay':
      return 'QPay'
    case 'storepay':
      return 'StorePay'
    case 'pocket':
      return 'PocketZero'
    case 'lend':
      return 'LendDy'
    case 'omniway':
      return 'OmniWay'
    case 'hipay':
      return 'HiPay'
    case 'simple':
      return 'Simple'
    case 'transfer':
      return 'Шилжүүлэг'
    case 'wallet':
      return 'Хэтэвч'
    default:
      return 'Тодорхойгүй'
  }
}

export function getSubscriptionStatusBadge(status: string) {
  switch (status) {
    case 'active':
      return <CustomBadge color="green">Идэвхтэй</CustomBadge>
    case 'inactive':  
      return <CustomBadge color="red">Идэвхгүй</CustomBadge>
    case 'expired':
      return <CustomBadge color="red">Хугацаа дууссан</CustomBadge>
    case 'suspended': 
      return <CustomBadge color="red">Хүчингүй болсон</CustomBadge>
    case 'grace':
      return <CustomBadge color="amber">Төлбөр гарсан</CustomBadge>
    default:
      return <CustomBadge>Тодорхойгүй</CustomBadge>
  }
}

export function getProductStatusBadge(status: string, size: any = 'default') {
  switch (status) {
    case 'published':
      return <CustomBadge size={size} color="green">Идэвхтэй</CustomBadge>
    case 'draft':  
      return <CustomBadge size={size}>Ноорог</CustomBadge>
    case 'archived':
      return <CustomBadge size={size} color="amber">Архив</CustomBadge>
    default:
      return <CustomBadge size={size}>Тодорхойгүй</CustomBadge>
  }
}

/*
  The status a customer or vendor should SEE, which is not always order.status.

  'pending' means "waiting for money". That is true of a normal unpaid order, and of a
  split-payment order whose deposit has not arrived — but not of an order where nothing
  is owed up front: a pay-after-delivery order, or a deposit order whose deposit has
  landed. Those are confirmed; the goods just have not moved yet.

  payAfterDelivery is exactly that condition. It is set at checkout for onDelivery orders
  and flipped by settleDeposit when a deposit lands, so this single test covers both.

  Derived rather than a real orderStatus enum value on purpose: 'confirmed' is not a
  fulfilment state — the order still has to travel ready -> shipped -> delivered — it is
  the payment axis reporting satisfied. Adding it to the enum would put a non-fulfilment
  value on the fulfilment ladder and break every status filter, report and webhook that
  walks it. See the two-axes note on Order.payAfterDelivery in the backend schema.
*/
export function resolveOrderStatus(order: any): string {
  if (order?.status !== 'pending') return order?.status
  if (order?.payAfterDelivery && !order?.paidAt) return 'confirmed'
  return 'pending'
}

// `type` only affects the 'delivered' wording: a pickup order that reaches it was
// collected in person, never delivered. Optional so callers holding just a status
// keep working — they fall back to the delivery wording.
export function getOrderStatusBadge(status: string, size: "default" | "sm" | "xs" | "lg" | null | undefined = 'default', variant: "default" | "outline" = 'default', type?: string) {
  switch (status) {
    case 'canceled':
      return <CustomBadge variant={variant} color="red" size={size}>Цуцлагдсан</CustomBadge>
    case 'refunded':
      return <CustomBadge variant={variant} color="rose" size={size}>Бүтэн буцаасан</CustomBadge>
    case 'partiallyRefunded':
      return <CustomBadge variant={variant} color="rose" size={size}>Хагас буцаасан</CustomBadge>
    case 'pending':
      return <CustomBadge variant={variant} color="amber" size={size}>Хүлээгдэж буй</CustomBadge>
    // Derived, never stored — see resolveOrderStatus.
    case 'confirmed':
      return <CustomBadge variant={variant} color="green" size={size}>Баталгаажсан</CustomBadge>
    case 'paid':
      return <CustomBadge variant={variant} color="green" size={size}>Төлөгдсөн</CustomBadge>
    case 'ready':
      return <CustomBadge variant={variant} color="blue" size={size}>Бэлэн болсон</CustomBadge>
    case 'shipped':
      return <CustomBadge variant={variant} color="purple" size={size}>Хүргэлтэнд гарсан</CustomBadge>
    case 'delivered':
      return <CustomBadge variant={variant} color="blue" size={size}>{type === 'pickup' ? 'Ирж авсан' : 'Хүргэгдсэн'}</CustomBadge>
    case 'deliveryFailed':
      return <CustomBadge variant={variant} color="red" size={size}>Хүргэлт амжилтгүй</CustomBadge>
    default:
      return <CustomBadge variant={variant} size={size}>Тодорхойгүй</CustomBadge>
  }
}

export function getOrderStatusText(status: string) {
  switch (status) {
    case 'canceled':
      return 'Цуцлагдсан'
    case 'refunded':
      return 'Бүтэн буцаасан'
    case 'partiallyRefunded':
      return 'Хагас буцаасан'
    case 'pending':
      return 'Хүлээгдэж буй'
    case 'confirmed':
      return 'Баталгаажсан'
    case 'paid':
      return 'Төлөгдсөн'
    case 'ready':
      return 'Бэлэн болсон'
    case 'shipped':
      return 'Хүргэлтэнд гарсан'
    case 'delivered':
      return 'Хүргэгдсэн'
    case 'deliveryFailed':
      return 'Хүргэлт амжилтгүй'
    default:
      return 'Тодорхойгүй'
  }
}

// Color per Booklo delivery statusCode (from the backend's parseBookloStatus).
const bookloStatusColors: Record<string, "green" | "red" | "amber" | "purple" | "blue" | "rose" | "default"> = {
  pending: "amber",
  in_delivery: "purple",
  delivered: "blue",
  delivery_failed: "red",
  postponed: "amber",
  canceled: "red",
  not_arrived: "amber",
}

// Shorter display labels for statusCodes whose Booklo string is too verbose for a
// badge. When set, this overrides Booklo's raw status text; otherwise we show it as-is.
const bookloStatusLabels: Record<string, string> = {
  postponed: "Хойшлогдсон",
}

// Order-status badge that shows Booklo's delivery status ONLY for orders actually
// dispatched through Booklo (order.bookloOrderId set) that have a synced status.
// This surfaces richer Booklo states (Бараа ирээгүй, Хойшлогдсон, …) instead of our
// coarser lifecycle status. Every other order falls back to getOrderStatusBadge.
export function getOrderDeliveryBadge(order: any, size: "default" | "sm" | "xs" | "lg" | null | undefined = 'default', variant: "default" | "outline" = 'default') {
  const booklo = order?.bookloStatus
  if (order?.bookloOrderId && booklo?.status) {
    const label = bookloStatusLabels[booklo.statusCode] ?? booklo.status
    return <CustomBadge variant={variant} color={bookloStatusColors[booklo.statusCode] ?? "default"} size={size}>{label}</CustomBadge>
  }
  return getOrderStatusBadge(resolveOrderStatus(order), size, variant, order?.type)
}

export const orderStatusList = [
  { value: 'pending', label: 'Хүлээгдэж буй' },
  { value: 'paid', label: 'Төлөгдсөн' },
  { value: 'ready', label: 'Бэлэн болсон' },
  { value: 'shipped', label: 'Хүргэлтэнд гарсан' },
  { value: 'delivered', label: 'Хүргэгдсэн' },
  { value: 'deliveryFailed', label: 'Хүргэлт амжилтгүй' },
  { value: 'canceled', label: 'Цуцлагдсан', disabled: true },
  { value: 'refunded', label: 'Бүтэн буцаасан', disabled: true },
  { value: 'partiallyRefunded', label: 'Хагас буцаасан', disabled: true },
]

export function getPaymentMethodIcon(method: string): { light: string; dark: string } | null {
  switch (method) {
    case 'qpay': return { light: '/qpay.svg', dark: '/qpay-white.svg' }
    case 'storepay': return { light: '/storepay.png', dark: '/storepay-white.png' }
    case 'pocket': return { light: '/pocket.png', dark: '/pocket.png' }
    case 'lend': return { light: '/lend-icon.svg', dark: '/lenddy-white.svg' }
    case 'omniway': return { light: '/omniway.png', dark: '/omniway.png' }
    case 'transfer': return { light: '/transfer.png', dark: '/transfer.png' }
    // Full-colour artwork, so one file carries both themes — same as transfer/pocket.
    case 'cash': return { light: '/cash.svg', dark: '/cash.svg' }
    case 'onDelivery': return { light: '/onDelivery.svg', dark: '/onDelivery.svg' }
    default: return null
  }
}

export function getOrderPaymentMethodText(status: string, short: boolean = false) {
  switch (status) {
    case 'qpay':
      return 'QPay'
    case 'storepay':
      return 'StorePay'
    case 'pocket':
      return 'PocketZero'
    case 'lend':
      return 'LendDy'
    case 'omniway':
      return 'OmniWay'
    case 'transfer':
      return short ? 'Шилжүүлэг' : 'Дансаар шилжүүлэх'
    case 'cash':
      return 'Бэлнээр'
    case 'onDelivery':
      return short ? 'Авахдаа' : 'Авахдаа төлөх'
    default:
      return 'Тодорхойгүй'
  }
}

// 'onDelivery' is the placeholder an unsettled pay-after-delivery order carries, and
// 'cash' is what it becomes once a courier takes notes at the door. Both are real
// stored values, so both are filterable.
export const orderPaymentMethodList = [
  { value: 'transfer', label: 'Шилжүүлэг' },
  { value: 'qpay', label: 'QPay' },
  { value: 'storepay', label: 'StorePay' },
  { value: 'pocket', label: 'PocketZero' },
  { value: 'lend', label: 'LendDy' },
  { value: 'omniway', label: 'OmniWay' },
  { value: 'cash', label: 'Бэлнээр' },
  { value: 'onDelivery', label: 'Төлбөр хүлээгдэж буй' },
]

// Is this order's money in hand? For every prepaid order the status badge already
// says so — nothing reaches "Бэлэн болсон" without passing "Төлөгдсөн" first. A
// pay-after-delivery order moves on both axes independently, so it needs its own
// badge; paidAt is the only trustworthy signal there.
export function isOrderUnsettled(order: any) {
  return !!order?.payAfterDelivery && !order?.paidAt
}

export function getOrderPaymentBadge(order: any, size: "default" | "sm" | "xs" | "lg" | null | undefined = 'default', variant: "default" | "outline" = 'default') {
  if (!order?.payAfterDelivery) return null
  /*
    This badge exists because on a pay-after-delivery order the status badge does NOT
    tell you whether the money arrived — 'delivered' says nothing about payment. When the
    status IS 'paid' it already says exactly that, and rendering both produced two
    identical "Төлөгдсөн" chips side by side. That pairing only became reachable with
    split payment, where clearing the balance early lands the order on 'paid' rather than
    on 'delivered'.
  */
  if (order?.status === 'paid') return null
  if (order?.paidAt) return <CustomBadge variant={variant} color="green" size={size}>Төлөгдсөн</CustomBadge>
  // Split payment, deposit in but balance outstanding. Blue rather than green (which
  // would falsely claim the order is settled) or amber (which means nothing has arrived).
  // It matches the "rail in play" tone used on the order detail page.
  if (order?.depositPaidAt) return <CustomBadge variant={variant} color="blue" size={size}>Урьдчилгаа төлсөн</CustomBadge>
  return <CustomBadge variant={variant} color="amber" size={size}>Төлөөгүй</CustomBadge>
}

/*
  Split payment (урьдчилгаа) accessors — mirrors of the backend's getDepositPaid /
  getOutstandingBalance / hasUnpaidDeposit in services/order.service.js.

  The balance is always derived, never stored: order.amount shrinks on a partial refund,
  so anything that caches a balance goes stale silently.

  These read order.depositAmount, NEVER vendor.canDeposit. The deposit is stamped on the
  order at checkout; the vendor's shop-wide rule is live and may already have changed.
*/
export const DEPOSIT_MIN_LEG = 50

export function isDepositOrder(order: any) {
  return !!(order?.depositAmount > 0)
}

export function getDepositPaid(order: any) {
  return order?.depositPaidAt && order?.depositAmount > 0 ? order.depositAmount : 0
}

export function getOrderBalance(order: any) {
  if (!order) return 0
  if (order.paidAt) return 0
  return Math.max(0, (order.amount || 0) - getDepositPaid(order))
}

// What the customer has actually handed over so far — not order.amount, which is what
// they owe in total. Used wherever a refund is being calculated against money received.
export function getOrderPaidSoFar(order: any) {
  if (order?.paidAt) return order.amount || 0
  return getDepositPaid(order)
}

export const depositTypeList = [
  { value: 'percent', label: 'Хувиар' },
  { value: 'amount', label: 'Тогтмол дүнгээр' },
]

export function getOrderTypeBadge(status: string, size: "default" | "sm" | "xs" | "lg" | null | undefined = 'default', variant: "default" | "outline" = 'default') {
  switch (status) {
    case 'delivery':
      return <CustomBadge variant={variant} color="blue" size={size}>Хүргэлтээр</CustomBadge>
    case 'pickup':
      return <CustomBadge variant={variant} color="rose" size={size}>Салбараас</CustomBadge>
    default:
      return <CustomBadge variant={variant} size={size}>Хүргэлтгүй</CustomBadge>
  }
}

export const orderTypeList = [
  { value: 'delivery', label: 'Хүргэлтээр' },
  { value: 'pickup', label: 'Салбараас' },
]

export function flattenCategories(items: any[]) {
  const allItems: any[] = [];

  function traverse(items: any[]) {
    items.forEach((item) => {
      allItems.push(item);
      if (item.children) {
        traverse(item.children);
      }
    });
  }

  traverse(items);

  return allItems;
}

export const deliveryFailedReasons = [
  { id: 'noPerson', name: 'Хүлээн авах хүн байгаагүй', description: 'Тухайн хүргэх газар хүлээн авах хүн байгаагүй', selected: false },
  { id: 'noDial', name: 'Холбоо барих боломжгүй байсан', description: 'Захиалга дээрх холбоо барих дугаар холбогдоогүй', selected: false, },
  { id: 'wrongAddress', name: 'Захиалгын хаяг буруу / олдоогүй', description: 'Захиалгын тухайн хаяг нь байдаггүй эсвэл олдоогүй', selected: false },
  { id: 'other', name: 'Бусад шалтгаан', description: 'Шалтгаанаа бичиж оруулна уу', selected: false },
]

export const canceledReasons = [
  { id: 'fromCustomer', name: 'Үйлчлүүлэгчийн хүсэлтээр', description: 'Тухайн захиалга хийсэн үйлчлүүлэгч цуцлахыг хүссэн', selected: false, },
  { id: 'badInfo', name: 'Дутуу / Буруу мэдээлэл оруулсан', description: 'Захиалгын мэдээлэл нь дутуу эсвэл буруу', selected: false },
  { id: 'fake', name: 'Хуурамч захиалга', description: 'Үнэн, бодит мэдээлэл байхгүй хуурамч захиалга', selected: false },
  { id: 'other', name: 'Бусад шалтгаан', description: 'Шалтгаанаа бичиж оруулна уу', selected: false },
]


export function getGiftStatusBadge(status: string, size: "default" | "sm" | "xs" | "lg" | null | undefined = 'default') {
  switch (status) {
    case 'canceled':
      return <CustomBadge color="red" size={size}>Цуцлагдсан</CustomBadge>
    case 'pending':
      return <CustomBadge color="amber" size={size}>Хүлээгдэж буй</CustomBadge>
    case 'paid':
      return <CustomBadge color="green" size={size}>Төлөгдсөн</CustomBadge>
    case 'sent':  
      return <CustomBadge color="blue" size={size}>Илгээсэн</CustomBadge>
    case 'used':  
      return <CustomBadge color="purple" size={size}>Ашигласан</CustomBadge>
    default:
      return <CustomBadge size={size}>Тодорхойгүй</CustomBadge>
  }
}

export const giftStatusList = [
  { value: 'pending', label: 'Хүлээгдэж буй' },
  { value: 'paid', label: 'Төлөгдсөн' },
  { value: 'sent', label: 'Илгээсэн' },
  { value: 'used', label: 'Ашигласан' },
  { value: 'canceled', label: 'Цуцлагдсан' },
]

export const deliveryTypeList = [
  { value: 'constantDelivery', label: 'Хүргэлттэй (Үнэ тогтмол)' },
  { value: 'noDelivery', label: 'Хүргэлтгүй' },
  { value: 'minOrderAmount', label: 'Тодорхой үнийн дүнгээс дээш үнэгүй' },
  { value: 'laterDelivery', label: 'Хүргэлттэй (Үнэ тусдаа өөрсдөө тооцож авна)' },
  { value: 'freeDelivery', label: 'Бүх хүргэлт үнэгүй' },
]

export const designFontList = [
  { value: 'inter', label: 'Inter' },
  { value: 'gilroy', label: 'Gilroy' },
  { value: 'roboto', label: 'Roboto' },
  { value: 'montserrat', label: 'Montserrat' },
  { value: 'playpen', label: 'Playpen Sans' },
  { value: 'cormorant', label: 'Cormorant' },
]

// Mirrors storefront/utils/util.tsx getFontClass, including the font-inter
// fallback — used to render editor previews in the vendor's actual shop font.
// Every branch is emitted by the hidden font-* divs in components/settings/Design.tsx,
// so Tailwind's scanner keeps these classes in the build.
export const getFontClass = (font?: string) => {
  if (!font) return 'font-inter'

  switch (font.toLowerCase()) {
  case 'gilroy':
    return 'font-gilroy'
  case 'inter':
    return 'font-inter'
  case 'roboto':
    return 'font-roboto'
  case 'montserrat':
    return 'font-montserrat'
  case 'playpen':
    return 'font-playpen'
  case 'cormorant':
    return 'font-cormorant'
  default:
    return 'font-inter'
  }
}

export const designColorList = [
  { value: 'blue-500', label: 'Цэнхэр', className: 'text-blue-500' },
  { value: 'red-500', label: 'Цэнхэр', className: 'text-red-500' },
  { value: 'pink-500', label: 'Цэнхэр', className: 'text-pink-500' },
  { value: 'purple-500', label: 'Цэнхэр', className: 'text-purple-500' },
  { value: 'green-500', label: 'Цэнхэр', className: 'text-green-500' },
  { value: 'amber-500', label: 'Цэнхэр', className: 'text-amber-500' },
  { value: 'zinc-800', label: 'Хар', className: 'text-zinc-800' },
]

export const designBorderRadiusList = [
  { value: 'none', label: 'Өнцөгтэй' },
  { value: 'xs', label: 'Маш бага' },
  { value: 'sm', label: 'Бага' },
  { value: 'md', label: 'Дунд' },
  { value: 'lg', label: 'Их' },
  { value: 'xl', label: 'Маш их' },
]

export const designImageAspectList = [
  { value: '1/1', label: '1/1' },
  { value: '3/4', label: '3/4' },
  { value: '4/3', label: '4/3' },
  { value: '16/9', label: '16/9' },
  { value: '9/16', label: '9/16' },
]

export const orderExpireMinutesList = [
  { value: '5', label: '5 минут' },
  { value: '10', label: '10 минут' },
  { value: '30', label: '30 минут' },
  { value: '60', label: '1 цаг' },
  { value: '360', label: '6 цаг' },
  { value: '720', label: '12 цаг' },
  { value: '1440', label: '24 цаг' },
  { value: '2880', label: '48 цаг' },
  { value: '4320', label: '72 цаг' },
]

export function formatExpireTime(minutes: number) {
  if (minutes < 60) return `${minutes} минут`
  return `${minutes / 60} цаг`
}

export const refundReasonList = [
  { value: 'Бараа дууссан', label: 'Бараа дууссан' },
  { value: 'Захиалагчийн хүсэлтээр буцаасан', label: 'Захиалагчийн хүсэлтээр буцаасан' },
  { value: 'Буруу бараа', label: 'Буруу бараа' },
  { value: 'Бараа гэмтсэн', label: 'Бараа гэмтсэн' },
  { value: 'Бусад шалтгаан', label: 'Бусад шалтгаан' },
]

export const getTimeRemaining = (endDate: any, startDate: any = moment()) => {
  const start = moment(startDate)
  const end = moment(endDate)

  const totalMinutes = end.diff(start, 'minutes')
  const totalHours = end.diff(start, 'hours')

  if (totalMinutes <= 0) {
    return 'Дууссан'
  }

  if (totalHours >= 24) {
    return `${Math.ceil(totalHours / 24)} хоног`
  }

  if (totalHours > 0) {
    return `${Math.ceil(totalMinutes / 60)} цаг`
  }

  return `${totalMinutes} минут`
}

export function getAICreditBadge(status: string, size: "default" | "sm" | "xs" | "lg" | null | undefined = 'default') {
  switch (status) {
    case 'expired':
      return <CustomBadge color="red" size={size}>Хүчингүй</CustomBadge>
    case 'active':
      return <CustomBadge color="green" size={size}>Идэвхтэй</CustomBadge>
    case 'used':  
      return <CustomBadge color="purple" size={size}>Ашигласан</CustomBadge>
    case 'soon':  
      return <CustomBadge color="blue" size={size}>Эхлээгүй</CustomBadge>
    default:
      return <CustomBadge size={size}>Тодорхойгүй</CustomBadge>
  }
}

export const AI_CREDIT_PRICE = 10

export function getDomainInvoiceTypeText(type: string) {
  switch (type) {
    case 'registration':
      return 'Бүртгэл'
    case 'renewal':
      return 'Сунгалт'
    case 'transfer':
      return 'Шилжүүлэлт'
    case 'privacy':
      return 'Нууцлал'
    case 'other':
      return 'Бусад'
    default:
      return 'Тодорхойгүй'
  }
}

export function DanVerifiedIcon({ isVerified = false, placement = 'top', className }: { isVerified: boolean, placement?: string, className?: string }) {
  return (
    <div className="cursor-pointer">
      <Tooltip content={isVerified ? 'ДАН системээр баталгаажсан' : 'ДАН системээр баталгаажаагүй'} placement={placement}>
        { isVerified ? 
          <ShieldCheck className={`size-[18px] text-blue ${className || ''}`} strokeWidth={2.5} /> :
          <ShieldX className={`size-[18px] text-danger ${className || ''}`} strokeWidth={2.5} />
        }
      </Tooltip>
    </div>
  )
}

export function getMainDomain(vendor: any) {
  if(!vendor || !vendor.domain) return null
  if(vendor.domains && vendor.domains.length > 0) return vendor.domains?.[0]?.domain
  if(vendor.customDomain && vendor.isCustomDomainActive) return vendor.customDomain
  return `${vendor.domain}.shop.mn`
}

// This function calculates the new upgrade Plan Price.

export function getUpgradePlanPrice({ subscription, currentPlan, newPlan, months }: {
  subscription: { startDate: Date | string; renewedAt?: Date | string | null; invoice?: { totalAmount: number; months: number; isUpgradeDiscounted?: boolean } | null } | null;
  currentPlan: { id: string; slug: string; monthlyPrice: number; monthlyPriceSale?: number | null; annualPrice: number; annualPriceSale?: number | null; };
  newPlan: { id: string; slug: string; monthlyPrice: number; monthlyPriceSale?: number | null; annualPrice: number; annualPriceSale?: number | null };
  months: number;
}) {
  const isAnnual = months === 12

  const newTotalPrice = isAnnual
    ? (newPlan.annualPriceSale || newPlan.annualPrice)
    : (newPlan.monthlyPriceSale || newPlan.monthlyPrice) * months

  if (!currentPlan || !subscription) return { price: newTotalPrice, isDiscounted: false, usedDays: null }
  if (currentPlan.monthlyPrice > newPlan.monthlyPrice) return { price: newTotalPrice, isDiscounted: false, usedDays: null }
  if (currentPlan.id == newPlan.id) return { price: newTotalPrice, isDiscounted: false, usedDays: null }
  if (currentPlan.slug == 'free') return { price: newTotalPrice, isDiscounted: false, usedDays: null }
  if (!subscription?.invoice) return { price: newTotalPrice, isDiscounted: false, usedDays: null }
  if (subscription && subscription.invoice && subscription.invoice?.isUpgradeDiscounted) return { price: newTotalPrice, isDiscounted: false, usedDays: null }

  const startDate = moment(subscription.renewedAt || subscription.startDate)
  const usedDays = moment().diff(startDate, 'days') + 1

  if (usedDays >= 10) return { price: newTotalPrice, isDiscounted: false, usedDays }

  const currentTotalPaid = subscription.invoice.totalAmount
  const discountedPrice = newTotalPrice - currentTotalPaid + (currentTotalPaid * usedDays / 10)

  if (discountedPrice <= 0) return { price: 0, isDiscounted: true, usedDays }

  return { price: Math.round(discountedPrice), isDiscounted: true, usedDays }
}